import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  MoreVertical,
  Link as LinkIcon,
  Image as GalleryIcon,
  PlusCircle,
  LogOut,
  AlertCircle,
} from 'lucide-react'; // Using GalleryIcon for consistency, added LogOut, AlertCircle
import { format, parse, isValid } from 'date-fns';
import { fetchApi } from '@/lib/api'; // <-- Import fetchApi

// Define the expected shape of an event from the API
interface ApiEvent {
  id: number;
  user_id: number;
  name: string;
  event_date: string | null; // Matches sql.NullTime JSON marshal
  event_link_id: string;
  created_at: string;
}

// This route is automatically nested under /_authenticated
// so the beforeLoad check in _authenticated.tsx applies.
export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const response = await fetchApi('/api/auth/logout', { // <-- Use fetchApi
        method: 'POST',
      });
      if (!response.ok) {
        console.error('Logout failed on server:', response.status);
        // Optionally show an error to the user
      }
    } catch (error) {
      console.error('Logout request failed:', error);
      // Optionally show an error to the user
    } finally {
      // Always navigate regardless of API call success/failure for logout
      navigate({ to: '/login', replace: true });
    }
  };

  // Fetch recent events on mount
  useEffect(() => {
    const fetchRecentEvents = async () => {
      setLoadingEvents(true);
      setEventsError(null);
      try {
        // Fetch limited events (e.g., 4)
        const response = await fetchApi('/api/admin/events?limit=4'); // <-- Use fetchApi
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: ApiEvent[] = await response.json();
        setEvents(data);
      } catch (err) {
        console.error('Failed to fetch recent events:', err);
        setEventsError(
          err instanceof Error ? err.message : 'An unknown error occurred',
        );
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchRecentEvents();
  }, []);

  const formatDisplayDate = (dateStr: string | null): string => {
    if (dateStr === null || dateStr === '' || dateStr === '0001-01-01T00:00:00Z') {
      return 'Not Set';
    }
    try {
      const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
      return isValid(parsedDate) ? format(parsedDate, 'PPP') : 'Invalid Date';
    } catch (e) {
      console.error('Error parsing event_date:', e);
      return 'Invalid Date';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <Button variant="ghost" onClick={handleLogout} disabled={isLoggingOut}>
          <LogOut className="mr-2 h-4 w-4" />
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Events Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-medium">Recent Events</CardTitle>
            <Button asChild variant="outline" size="sm">
              <Link to="/events/new">
                <PlusCircle className="mr-2 h-4 w-4" /> Create New
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
        {loadingEvents && (
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
          </div>
        )}
        {eventsError && (
          <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{eventsError}</AlertDescription>
          </Alert>
        )}
        {!loadingEvents && !eventsError && events.length === 0 && (
              <p className="text-sm text-muted-foreground">No events created yet.</p>
        )}
        {!loadingEvents && !eventsError && events.length > 0 && (
              <ul className="space-y-2">
            {events.map((event) => (
                  <li key={event.id} className="flex items-center justify-between border-b pb-2 last:border-none">
                    <div>
                      <p className="font-medium">{event.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Date: {formatDisplayDate(event.event_date)}
                      </p>
                    </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link
                          to="/events/$eventId/share"
                            params={{ eventId: String(event.id) }}
                        >
                            <LinkIcon className="mr-2 h-4 w-4" /> Link/QR
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          to="/events/$eventId/gallery"
                            params={{ eventId: String(event.id) }}
                        >
                            <GalleryIcon className="mr-2 h-4 w-4" /> Gallery
                        </Link>
                      </DropdownMenuItem>
                        {/* Add Edit/Delete later if needed */}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
          <CardFooter>
             <Button asChild variant="link" className="text-sm p-0 h-auto">
               <Link to="/events">View All Events</Link>
             </Button>
                </CardFooter>
              </Card>

        {/* Placeholder for another card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium">Statistics</CardTitle>
            <CardDescription>Coming soon...</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">(Analytics will be displayed here)</p>
          </CardContent>
        </Card>
          </div>
    </div>
  );
}
